import { useState } from 'react';
import { Alert } from '@goodsteward/gs-ui';
import {
    useStripeAccount,
    useStartStripeOnboarding,
    useRefreshStripeStatus,
    useDisconnectStripe,
} from '@/api/stripeAccount';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle,
    DialogDescription, DialogFooter, DialogClose,
} from '@/components/ui/Dialog';

// Stripe Connect status + onboarding controls, rendered inside
// BillingSettingsPanel. Phase 0b of docs/build-plans/pma-stripe-payments.md.
//
// Identical shape to accounting-client's PaymentsSettingsPage and Home's
// StripeSettings — they all read the same admin-server endpoint. The
// difference is the URL Stripe redirects back to: PMA's pair lives at
// /settings/stripe/{return,refresh} and is wired in router.tsx.

export function StripeConnectionCard() {
    const status = useStripeAccount();
    const start = useStartStripeOnboarding();
    const refresh = useRefreshStripeStatus();
    const disconnect = useDisconnectStripe();
    const [actionError, setActionError] = useState<string | null>(null);
    const [confirmDisconnect, setConfirmDisconnect] = useState(false);

    async function connectNow() {
        setActionError(null);
        try {
            const result = await start.mutateAsync();
            window.location.href = result.url;
        } catch (e) {
            setActionError(e instanceof Error ? e.message : 'Could not start onboarding.');
        }
    }

    async function refreshNow() {
        setActionError(null);
        try {
            await refresh.mutateAsync();
        } catch (e) {
            setActionError(e instanceof Error ? e.message : 'Could not refresh status from Stripe.');
        }
    }

    async function performDisconnect() {
        setActionError(null);
        try {
            await disconnect.mutateAsync();
            setConfirmDisconnect(false);
        } catch (e) {
            setConfirmDisconnect(false);
            setActionError(e instanceof Error ? e.message : 'Could not disconnect.');
        }
    }

    if (status.isLoading) {
        return <div className="p-4 text-sm text-text-subtle">Loading Stripe status…</div>;
    }
    if (status.isError) {
        return (
            <Alert variant="danger">
                {status.error instanceof Error ? status.error.message : 'Could not load Stripe status.'}
            </Alert>
        );
    }

    const data = status.data!;
    const ready = !!data.charges_enabled;
    const onboardingComplete = !!data.details_submitted;
    const stripeDashUrl = data.stripe_user_id
        ? `https://dashboard.stripe.com/${data.stripe_user_id}`
        : 'https://dashboard.stripe.com';

    return (
        <section>
            <header className="mb-4">
                <h2 className="text-base font-medium text-text mb-1">Practice payments (Stripe)</h2>
                <p className="text-sm text-text-subtle">
                    Connect your practice's Stripe account to accept invoice payments online.
                </p>
            </header>

            {actionError && <Alert variant="danger" className="mb-4">{actionError}</Alert>}

            {!data.connected ? (
                <button
                    type="button"
                    onClick={connectNow}
                    disabled={start.isPending}
                    className="px-4 py-2 rounded-md bg-accent-dark text-text-inverse text-sm hover:bg-accent-text disabled:opacity-50"
                >
                    {start.isPending ? 'Starting…' : 'Connect with Stripe'}
                </button>
            ) : (
                <div className="space-y-3">
                    <div className="flex items-start justify-between gap-3">
                        <div>
                            <p className="text-sm font-medium text-text">
                                {ready ? 'Ready to accept payments' :
                                 onboardingComplete ? 'Submitted — waiting for Stripe verification' :
                                 'Onboarding incomplete'}
                            </p>
                            <p className="text-xs text-text-subtle font-mono mt-1">
                                Account: {data.stripe_user_id}
                            </p>
                        </div>
                        <StatusBadge ready={ready} onboardingComplete={onboardingComplete} />
                    </div>

                    <dl className="grid grid-cols-3 gap-3 text-sm">
                        <Capability label="Charges enabled"   on={!!data.charges_enabled} />
                        <Capability label="Payouts enabled"   on={!!data.payouts_enabled} />
                        <Capability label="Details submitted" on={!!data.details_submitted} />
                    </dl>

                    <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-border">
                        {!onboardingComplete && (
                            <button
                                type="button"
                                onClick={connectNow}
                                disabled={start.isPending}
                                className="px-3 py-2 rounded-md bg-accent-dark text-text-inverse text-sm hover:bg-accent-text disabled:opacity-50"
                            >
                                {start.isPending ? 'Resuming…' : 'Continue onboarding'}
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={refreshNow}
                            disabled={refresh.isPending}
                            // The cached row only updates when admin-server
                            // receives Stripe's account.updated webhook. If
                            // that webhook never landed (stripe listen wasn't
                            // running for admin-server during onboarding, the
                            // wrong --forward-* flag, a stale whsec_…), this
                            // button is the manual catch-up: POST to
                            // admin-server's /refresh-status, which calls
                            // stripe.accounts.retrieve and persists the live
                            // capability flags.
                            className="px-3 py-2 rounded-md border border-border text-sm hover:bg-surface-muted disabled:opacity-50"
                            title="Re-fetch this account's state from Stripe (use if charges/payouts look stale after onboarding)"
                        >
                            {refresh.isPending ? 'Refreshing…' : 'Refresh status'}
                        </button>
                        <a
                            href={stripeDashUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="px-3 py-2 rounded-md border border-border text-sm hover:bg-surface-muted"
                        >
                            Manage in Stripe
                        </a>
                        <button
                            type="button"
                            onClick={() => setConfirmDisconnect(true)}
                            disabled={disconnect.isPending}
                            className="ml-auto px-3 py-2 rounded-md border border-border text-error-text text-sm hover:bg-error-soft disabled:opacity-50"
                        >
                            {disconnect.isPending ? 'Disconnecting…' : 'Disconnect'}
                        </button>
                    </div>
                </div>
            )}

            <Dialog open={confirmDisconnect} onOpenChange={setConfirmDisconnect}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Disconnect Stripe?</DialogTitle>
                        <DialogDescription>
                            Future invoices won't be sent via Stripe. Your Stripe account itself
                            stays — you can manage it at dashboard.stripe.com.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <DialogClose asChild>
                            <button
                                type="button"
                                className="px-4 py-2 rounded-md border border-border text-text hover:bg-surface-muted"
                            >
                                Cancel
                            </button>
                        </DialogClose>
                        <button
                            type="button"
                            onClick={performDisconnect}
                            disabled={disconnect.isPending}
                            className="bg-error-soft border border-error text-error-text px-4 py-2 rounded-md hover:bg-error hover:text-text-inverse disabled:opacity-50"
                        >
                            {disconnect.isPending ? 'Disconnecting…' : 'Disconnect'}
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </section>
    );
}

function StatusBadge({ ready, onboardingComplete }: { ready: boolean; onboardingComplete: boolean }) {
    if (ready) {
        return <span className="px-1.5 py-0.5 rounded text-2xs bg-success-soft text-success-text">Live</span>;
    }
    if (onboardingComplete) {
        return <span className="px-1.5 py-0.5 rounded text-2xs bg-warning-soft text-warning-text">Pending Stripe</span>;
    }
    return <span className="px-1.5 py-0.5 rounded text-2xs bg-warning-soft text-warning-text">Action needed</span>;
}

function Capability({ label, on }: { label: string; on: boolean }) {
    return (
        <div>
            <dt className="text-xs text-text-subtle">{label}</dt>
            <dd className={`text-sm font-medium ${on ? 'text-success-text' : 'text-text-subtle'}`}>
                {on ? 'Yes' : 'No'}
            </dd>
        </div>
    );
}
